import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/* input[type=date] понимает только YYYY-MM-DD, а в базе встречается и 01.01.1980 */
const toDateInput = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const dotted = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return dotted ? `${dotted[3]}-${dotted[2]}-${dotted[1]}` : '';
};

const buildForm = (user) => ({
  username: user?.username || '',
  full_name: user?.full_name || '',
  password: '',
  role: user?.role || 'waiter',
  email: user?.email || '',
  phone: user?.phone || '',
  birth_date: toDateInput(user?.birth_date)
});

const UserFormModal = ({ user, roles = [], isSelf, onSubmit, onClose }) => {
  const isNew = !user;
  const [form, setForm] = useState(() => buildForm(user));
  const [touched, setTouched] = useState({});
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [saving, setSaving] = useState(false);

  const errors = {};
  if (!form.full_name.trim()) errors.full_name = 'Укажите ФИО';
  if (isNew && !/^[a-z0-9_.-]{3,}$/i.test(form.username)) {
    errors.username = 'Минимум 3 символа: латиница, цифры, точка, дефис';
  }
  if (isNew && form.password.length < 6) errors.password = 'Минимум 6 символов';
  if (!isNew && form.password && form.password.length < 6) errors.password = 'Минимум 6 символов';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Похоже на опечатку';

  const canSubmit = Object.keys(errors).length === 0;

  const update = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
  };

  // Пустая форма не должна краснеть до того, как её начали заполнять
  const markTouched = (field) => () => setTouched(prev => ({ ...prev, [field]: true }));
  const errorOf = (field) => (touched[field] ? errors[field] : undefined);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || saving) return;

    const payload = {
      full_name: form.full_name.trim(),
      role: form.role,
      email: form.email.trim(),
      phone: form.phone.trim(),
      birth_date: form.birth_date
    };
    if (isNew) {
      payload.username = form.username.trim();
      payload.password = form.password;
    } else if (form.password) {
      payload.password = form.password;
    }

    setSaving(true);
    setErrorCode('');
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Не удалось сохранить');
      setErrorCode(err?.response?.data?.code || '');
      setSaving(false);
    }
  };

  return (
    <div className="ui-modal" onClick={onClose} role="presentation">
      <div className="ui-modal-box wide" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ui-modal-head">
          <h2>{isNew ? 'Новый пользователь' : 'Изменить пользователя'}</h2>
          <button className="ui-icon-btn" type="button" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M14.5 9.50002L9.5 14.5M9.49998 9.5L14.5 14.5" />
            </svg>
          </button>
        </div>

        <form className="ui-form ui-grid" onSubmit={handleSubmit} autoComplete="off">
          <div className="ui-field">
            <label htmlFor="usr-name">ФИО</label>
            <input
              id="usr-name"
              type="text"
              value={form.full_name}
              onChange={update('full_name')}
              placeholder="Иванов Иван Иванович"
              onBlur={markTouched('full_name')}
              aria-invalid={Boolean(errorOf('full_name'))}
            />
            {errorOf('full_name') && <small className="ui-hint-error">{errorOf('full_name')}</small>}
          </div>

          <div className="ui-field">
            <label htmlFor="usr-login">Логин</label>
            <input
              id="usr-login"
              type="text"
              value={form.username}
              onChange={update('username')}
              placeholder="ivanov"
              disabled={!isNew}
              onBlur={markTouched('username')}
              aria-invalid={Boolean(errorOf('username'))}
            />
            {errorOf('username')
              ? <small className="ui-hint-error">{errorOf('username')}</small>
              : !isNew && <small className="usr-hint">Логин менять нельзя</small>}
          </div>

          <div className="ui-field">
            <label htmlFor="usr-role">Роль</label>
            <select id="usr-role" value={form.role} onChange={update('role')} disabled={isSelf}>
              {roles.map(role => (
                <option key={role.code} value={role.code}>{role.name}</option>
              ))}
            </select>
            {isSelf
              ? <small className="usr-hint">Свою роль сменить нельзя</small>
              : <small className="usr-hint">Доступ к разделам настраивается во вкладке «Роли и доступ»</small>}
          </div>

          <div className="ui-field">
            <label htmlFor="usr-password">{isNew ? 'Пароль' : 'Новый пароль'}</label>
            <input
              id="usr-password"
              type="password"
              value={form.password}
              onChange={update('password')}
              placeholder={isNew ? 'Минимум 6 символов' : 'Оставьте пустым'}
              autoComplete="new-password"
              onBlur={markTouched('password')}
              aria-invalid={Boolean(errorOf('password'))}
            />
            {errorOf('password')
              ? <small className="ui-hint-error">{errorOf('password')}</small>
              : !isNew && <small className="usr-hint">Заполните, чтобы сбросить пароль</small>}
          </div>

          <div className="ui-field">
            <label htmlFor="usr-email">Email</label>
            <input
              id="usr-email"
              type="email"
              value={form.email}
              onChange={update('email')}
              placeholder="name@example.com"
              onBlur={markTouched('email')}
              aria-invalid={Boolean(errorOf('email'))}
            />
            {errorOf('email') && <small className="ui-hint-error">{errorOf('email')}</small>}
          </div>

          <div className="ui-field">
            <label htmlFor="usr-phone">Телефон</label>
            <input
              id="usr-phone"
              type="tel"
              value={form.phone}
              onChange={update('phone')}
              placeholder="+7 900 000-00-00"
            />
          </div>

          <div className="ui-field">
            <label htmlFor="usr-birth">Дата рождения</label>
            <input id="usr-birth" type="date" value={form.birth_date} onChange={update('birth_date')} />
          </div>

          {error && (
            <div className="ui-inline-error wide">
              {error}
              {errorCode === 'employee_limit_reached' && (
                <Link to="/billing" className="ui-btn-primary usr-upgrade-link">Сменить тариф</Link>
              )}
            </div>
          )}

          <div className="ui-modal-actions wide">
            <button type="button" className="ui-btn-ghost" onClick={onClose} disabled={saving}>
              Отмена
            </button>
            <button type="submit" className="ui-btn-primary" disabled={!canSubmit || saving}>
              {saving ? 'Сохраняем…' : isNew ? 'Создать' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
